import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ChatView from './components/ChatView';
import FlowTree from './components/FlowTree';
import { ChatSession } from './services/tauri-api';
import { getChatList, getChatSession, getTreeStructure } from './services/tauri-api';
import { AITreeResponse } from './types';
import { initializeTagsConfig, extractLastUserMessageSync } from './utils/extractLastUserMessage';
import { addSessionChatId, hasSessionChatId, getSessionChatIds } from './utils/sessionStorage';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentChat, setCurrentChat] = useState<ChatSession | null>(null);
  const [treeData, setTreeData] = useState<AITreeResponse | null>(null);
  const [showTree, setShowTree] = useState(false);
  const [currentPromptSessionId, setCurrentPromptSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllSiblings, setShowAllSiblings] = useState(true);
  const [loadVersion, setLoadVersion] = useState<number>(0);

  // アプリ起動時にタグ設定を初期化
  useEffect(() => {
    initializeTagsConfig().catch(err => {
      console.error('タグ設定の初期化に失敗:', err);
    });
  }, []);

  // チャット一覧を読み込み
  const loadChats = useCallback(async () => {
    try {
      const response = await getChatList();
      if (response.success && response.data) {
        // セッションストレージに保存されたチャットIDのみをフィルタリング
        const sessionChatIds = getSessionChatIds();
        const filteredChats = response.data.filter(chat => 
          sessionChatIds.includes(chat.chat_id)
        );
        
        if (filteredChats.length > 0) {
          // 最初のチャットを選択
          const firstChat = filteredChats[0];
          const chatResponse = await getChatSession(firstChat.chat_id);
          if (chatResponse.success && chatResponse.data) {
            setCurrentChat(chatResponse.data);
          }
        }
      }
    } catch (err) {
      console.error('チャット読み込みエラー:', err);
      setError('チャットの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // チャット選択時の処理
  const handleChatSelect = useCallback(async (chatId: string) => {
    try {
      // セッションストレージに保存されたチャットIDかどうかをチェック
      if (!hasSessionChatId(chatId)) {
        console.warn('[App] Chat ID not in session storage:', chatId);
        return;
      }
      
      const response = await getChatSession(chatId);
      if (response.success && response.data) {
        setCurrentChat(response.data);
        setShowTree(false);
        setCurrentPromptSessionId(null);
        setSidebarOpen(false);
      }
    } catch (error) {
      console.error('チャット選択エラー:', error);
    }
  }, []);

  // AI返答をクリックした時の処理
  const handleAIMessageClick = useCallback(async (promptSessionId: string) => {
    try {
      setLoading(true);
      const response = await getTreeStructure(promptSessionId);
      if (response.success && response.data) {
        setTreeData(response.data);
        setCurrentPromptSessionId(promptSessionId);
        setShowTree(true);
      } else {
        setError(response.error || 'ツリー構造の読み込みに失敗しました');
      }
    } catch (err) {
      console.error('ツリー読み込みエラー:', err);
      setError('ツリー構造の読み込みに失敗しました');
    } finally {
      setLoading(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setLoadVersion(v => v + 1);
          }, 60);
        });
      });
    }
  }, []);

  // ツリーを閉じる
  const handleCloseTree = useCallback(() => {
    setShowTree(false);
    setTreeData(null);
    setCurrentPromptSessionId(null);
  }, []);

  // ツリー再読み込み
  const reloadTree = useCallback(async () => {
    if (!currentPromptSessionId) return;
    setLoading(true);
    try {
      const response = await getTreeStructure(currentPromptSessionId);
      if (response.success && response.data) {
        setTreeData(response.data);
      }
    } catch (err) {
      console.error('ツリー再読み込みエラー:', err);
    } finally {
      setLoading(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setLoadVersion(v => v + 1);
          }, 60);
        });
      });
    }
  }, [currentPromptSessionId]);

  // 新規チャット作成
  const handleCreateChat = useCallback(async () => {
    try {
      const { createChatSession } = await import('./services/tauri-api');
      const response = await createChatSession();
      if (response.success && response.data) {
        const chatId = response.data.chat_id;
        // セッションストレージにチャットIDを保存
        addSessionChatId(chatId);
        // 新しいチャットを選択
        await handleChatSelect(chatId);
        // チャット一覧を再読み込み
        await loadChats();
      } else {
        console.error('チャット作成失敗:', response.error);
      }
    } catch (error) {
      console.error('チャット作成エラー:', error);
    }
  }, [handleChatSelect, loadChats]);

  // メッセージ送信後の処理
  const handleMessageSent = useCallback(() => {
    // チャットを再読み込み
    if (currentChat) {
      handleChatSelect(currentChat.chat_id);
    }
    // チャット一覧（サイドバー）も更新
    loadChats();
  }, [currentChat, handleChatSelect, loadChats]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  if (loading && !currentChat) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">データを読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error && !currentChat) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">エラーが発生しました</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* サイドバー */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        showAllSiblings={showAllSiblings}
        onToggleShowAll={setShowAllSiblings}
        onChatSelect={handleChatSelect}
      />
      
      {/* メインコンテンツ */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* ヘッダー */}
        <Header 
          onMenuClick={() => setSidebarOpen(true)}
          onCreateChat={handleCreateChat}
        />
        
        {/* メインコンテンツエリア */}
        <main className="flex-1 overflow-hidden relative">
          {showTree && treeData ? (
            // ツリービュー表示
            <div className="absolute inset-0 bg-white z-10">
              <div className="h-full flex flex-col">
                {/* ツリービューのヘッダー */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCloseTree}
                      className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <h2 className="text-lg font-semibold text-gray-900">ツリービュー</h2>
                    {treeData?.prompt && (() => {
                      const userMessage = extractLastUserMessageSync(treeData.prompt);
                      const displayText = userMessage.length > 50 
                        ? userMessage.substring(0, 50) + '...' 
                        : userMessage;
                      return (
                        <span className="text-sm text-gray-500 truncate max-w-md" title={userMessage}>
                          ({displayText})
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {/* FlowTree */}
                <div className="flex-1 overflow-hidden">
                  <FlowTree
                    tree={treeData}
                    showAllSiblings={showAllSiblings}
                    onTreeReload={reloadTree}
                    onToggleShowAll={setShowAllSiblings}
                    sessionId={currentPromptSessionId || undefined}
                    loadVersion={loadVersion}
                    sidebarOpen={sidebarOpen}
                  />
                </div>
              </div>
            </div>
          ) : (
            // チャットビュー表示
            currentChat && (
              <ChatView
                chat={currentChat}
                onAIMessageClick={handleAIMessageClick}
                onMessageSent={handleMessageSent}
              />
            )
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
